import React, { useReducer, useEffect } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  PageContent,
  Container,
  Row,
  Col,
  Field,
  Heading,
  Button,
  Select,
  Checkbox,
  Datepicker,
} from 'components';
import { globalPropTypes } from 'helpers';
import reducer, { defaultState, updateFieldCreator, setEditDataCreator } from './reducer';
import { Margin } from 'styled-components-spacing';
import clearErrors from 'helpers/clearErrors';
import {
  setMaCisEditErrorsCreator,
  updateMaCisCreator,
  loadMaCisDetailsCreator,
  removeMaCisDetailsCreator,
  loadGlobalCisCertificationBodyDetailsAttributesCreator,
} from '../../../../../store/actionsCreators';
import { getUsersOptions } from '../../../../../api/services/user';
import { getCbCisUsers } from '../../../../../api/services/cis';
import DebounceMultiSelect from '../../../../../components/DebounceSelect/DebounceMultiSelect';
import { toApiDate } from 'helpers/date';

const CertificationBodyDetails = ({
  certificationBody,
  errors,
  updateCertificationBody,
  removeError,
  countries,
  memberAssociations,
  loadCertificationBodiesAttributes,
  loadCertificationBodyDetails,
  id,
  levelDomains,
}) => {
  const { t } = useTranslation();
  const [state, dispatch] = useReducer(reducer, defaultState);
  const history = useHistory();

  useEffect(() => {
    loadCertificationBodyDetails(id, history);
  }, []);

  useEffect(() => {
    if (certificationBody) {
      dispatch(setEditDataCreator(certificationBody));
    }
  }, [certificationBody]);

  useEffect(() => {
    loadCertificationBodiesAttributes();
  }, []);

  useEffect(() => {
    const loadCisUsers = async () => {
      if (!id) return;
      try {
        const res = await (await getCbCisUsers(id)).json();
        const users = (res?.results?.result || []).map((item) => ({
          value: item.value,
          label: item.label,
          approved: item.approved,
        }));
        dispatch(updateFieldCreator({ name: 'cisUsers', value: users }));
      } catch (err) {
        console.error('Failed to load CIS users', err);
      }
    };

    loadCisUsers();
  }, [id]);

  const handleChangeNative = (name, errorName) => (e) => {
    dispatch(updateFieldCreator({ name, value: e.target.value }));
    if (errors[errorName]) removeError(clearErrors(errors, errorName));
  };

  const handleChangeCustom = (name, errorName) => (value) => {
    dispatch(updateFieldCreator({ name, value }));
    if (errors[errorName]) removeError(clearErrors(errors, errorName));
  };

  const handleChangeSelect = (name, errorName) => (option) => {
    dispatch(updateFieldCreator({ name, value: option?.value }));
    if (errors[errorName]) removeError(clearErrors(errors, errorName));
  };

  const handleChangeNestedNative = (group, name, errorName) => (e) => {
    dispatch(
      updateFieldCreator({ name: group, value: { ...state[group], [name]: e.target.value } }),
    );
    if (errors[errorName]) removeError(clearErrors(errors, errorName));
  };

  const handleChangeNestedSelect = (group, name, errorName) => (option) => {
    dispatch(
      updateFieldCreator({ name: group, value: { ...state[group], [name]: option?.value } }),
    );
    if (errors[errorName]) removeError(clearErrors(errors, errorName));
  };

  const submit = () => {
    const body = {
      country: state.country,
      memberAssociation: state.memberAssociation,
      initials: state.initials,
      name: state.name,
      cbHeadName: state.cbHeadName,
      cbHeadEmail: state.cbHeadEmail,
      cbHeadTelephone: state.cbHeadTelephone,
      contactName: state.contactName,
      contactEmail: state.contactEmail,
      contactTelephone: state.contactTelephone,
      vatId: state.vatId,
      isActive: state.isActive,
      address: {
        line1: state.address?.line1,
        line2: state.address?.line2,
        postCode: state.address?.postCode,
        city: state.address?.city,
        country: state.address?.country,
      },
      cisUsers: state.cisUsers.map((option) => option.value),
      certificationBodyLevelDomains: state.certificationBodyLevelDomains,
      lcAgreementSigned: state.lcAgreementSigned,
      lcValidationSigned: state.lcValidationSigned,
      deltaAgreementSigned: state.deltaAgreementSigned,
      iso9001AccreditationSigned: state.iso9001AccreditationSigned,
      iso17024AccreditationSigned: state.iso17024AccreditationSigned,
      cctAgreementSigned: state.cctAgreementSigned,
      lcAgreementSignedAt: state.lcAgreementSigned
        ? state.lcAgreementSignedAt
          ? toApiDate(state.lcAgreementSignedAt)
          : null
        : null,
      lcValidationSignedAt: state.lcValidationSigned
        ? state.lcValidationSignedAt
          ? toApiDate(state.lcValidationSignedAt)
          : null
        : null,
      deltaAgreementSignedAt: state.deltaAgreementSigned
        ? state.deltaAgreementSignedAt
          ? toApiDate(state.deltaAgreementSignedAt)
          : null
        : null,
      iso9001AccreditationSignedAt: state.iso9001AccreditationSigned
        ? state.iso9001AccreditationSignedAt
          ? toApiDate(state.iso9001AccreditationSignedAt)
          : null
        : null,
      iso17024AccreditationSignedAt: state.iso17024AccreditationSigned
        ? state.iso17024AccreditationSignedAt
          ? toApiDate(state.iso17024AccreditationSignedAt)
          : null
        : null,
      cctAgreementSignedAt: state.cctAgreementSigned
        ? state.cctAgreementSignedAt
          ? toApiDate(state.cctAgreementSignedAt)
          : null
        : null,
    };

    if (state.invoiceAddressSame) {
      body.invoiceAddressSame = state.invoiceAddressSame;
    } else {
      body.invoiceAddress = {
        line1: state.invoiceAddress?.line1,
        line2: state.invoiceAddress?.line2,
        postCode: state.invoiceAddress?.postCode,
        city: state.invoiceAddress?.city,
        country: state.invoiceAddress?.country,
      };
    }

    updateCertificationBody(state.id, body, history);
  };

  return (
    <PageContent>
      <Container>
        <form>
          <Margin bottom="24">
            <Heading size="h5">
              {t('page.user.cis.certificationBodies.editCertificationBody')}
            </Heading>
          </Margin>
          <Row>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="country"
                  label={t('page.user.cis.certificationBodies.country')}
                  value={countries.find((option) => option.value === state.country) || ''}
                  clear={() => handleChangeSelect('country')(null)}
                  required
                  error={errors.country}
                >
                  <Select
                    disabled
                    id="country"
                    type="text"
                    placeholder={t('page.user.cis.certificationBodies.countryPlaceholder')}
                    value={countries.find((option) => option.value === state.country) || ''}
                    options={countries}
                    onChange={handleChangeSelect('country', 'country')}
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="memberAssociation"
                  label={t('page.user.cis.certificationBodies.memberAssociation')}
                  value={
                    memberAssociations.find((option) => option.value === state.memberAssociation) ||
                    ''
                  }
                  clear={() => handleChangeSelect('memberAssociation')(null)}
                  required
                  error={errors.memberAssociation}
                >
                  <Select
                    disabled
                    id="memberAssociation"
                    type="text"
                    placeholder={t(
                      'page.user.cis.certificationBodies.memberAssociationPlaceholder',
                    )}
                    value={
                      memberAssociations.find(
                        (option) => option.value === state.memberAssociation,
                      ) || ''
                    }
                    options={memberAssociations}
                    onChange={handleChangeSelect('memberAssociation', 'memberAssociation')}
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="initials"
                  label={t('page.user.cis.certificationBodies.initials')}
                  error={errors.initials}
                >
                  <input
                    id="initials"
                    type="text"
                    placeholder={t('page.user.cis.certificationBodies.initialsPlaceholder')}
                    value={state.initials}
                    onChange={handleChangeNative('initials', 'initials')}
                  />
                </Field>
              </Margin>
            </Col>
          </Row>
          <Row>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="name"
                  label={t('page.user.cis.certificationBodies.name')}
                  required
                  error={errors.name}
                >
                  <input
                    id="name"
                    type="text"
                    placeholder={t('page.user.cis.certificationBodies.namePlaceholder')}
                    value={state.name}
                    onChange={handleChangeNative('name', 'name')}
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field id="readonly-maId" label={t('page.user.cis.certificationBodies.maId')}>
                  <input
                    id="readonly-maId"
                    type="text"
                    disabled
                    readOnly
                    placeholder={t('page.user.cis.certificationBodies.maId')}
                    value={certificationBody?.memberAssociation?.id}
                    onChange={null}
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="vatId"
                  label={t('page.user.cis.certificationBodies.vatId')}
                  error={errors.vatId}
                >
                  <input
                    id="vatId"
                    type="text"
                    placeholder={t('page.user.cis.certificationBodies.vatIdPlaceholder')}
                    value={state.vatId}
                    onChange={handleChangeNative('vatId', 'vatId')}
                  />
                </Field>
              </Margin>
            </Col>
          </Row>
          <Row>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="cbHeadName"
                  label={t('page.user.cis.certificationBodies.cbHeadName')}
                  required
                  error={errors.cbHeadName}
                >
                  <input
                    id="cbHeadName"
                    type="text"
                    placeholder={t('page.user.cis.certificationBodies.cbHeadNamePlaceholder')}
                    value={state.cbHeadName}
                    onChange={handleChangeNative('cbHeadName', 'cbHeadName')}
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="cbHeadEmail"
                  label={t('page.user.cis.certificationBodies.cbHeadEmail')}
                  required
                  error={errors.cbHeadEmail}
                >
                  <input
                    id="cbHeadEmail"
                    type="email"
                    placeholder={t('page.user.cis.certificationBodies.cbHeadEmailPlaceholder')}
                    value={state.cbHeadEmail}
                    onChange={handleChangeNative('cbHeadEmail', 'cbHeadEmail')}
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="cbHeadTelephone"
                  label={t('page.user.cis.certificationBodies.cbHeadTelephone')}
                  required
                  error={errors.cbHeadTelephone}
                >
                  <input
                    id="cbHeadTelephone"
                    type="tel"
                    placeholder={t('page.user.cis.certificationBodies.cbHeadTelephonePlaceholder')}
                    value={state.cbHeadTelephone}
                    onChange={handleChangeNative('cbHeadTelephone', 'cbHeadTelephone')}
                  />
                </Field>
              </Margin>
            </Col>
          </Row>
          <Row>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="contactName"
                  label={t('page.user.cis.certificationBodies.contactName')}
                  error={errors.contactName}
                >
                  <input
                    id="contactName"
                    type="text"
                    placeholder={t('page.user.cis.certificationBodies.contactNamePlaceholder')}
                    value={state.contactName}
                    onChange={handleChangeNative('contactName', 'contactName')}
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="contactEmail"
                  label={t('page.user.cis.certificationBodies.contactEmail')}
                  error={errors.contactEmail}
                >
                  <input
                    id="contactEmail"
                    type="email"
                    placeholder={t('page.user.cis.certificationBodies.contactEmailPlaceholder')}
                    value={state.contactEmail}
                    onChange={handleChangeNative('contactEmail', 'contactEmail')}
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="contactTelephone"
                  label={t('page.user.cis.certificationBodies.contactTelephone')}
                  error={errors.contactTelephone}
                >
                  <input
                    id="contactTelephone"
                    type="tel"
                    placeholder={t('page.user.cis.certificationBodies.contactTelephonePlaceholder')}
                    value={state.contactTelephone}
                    onChange={handleChangeNative('contactTelephone', 'contactTelephone')}
                  />
                </Field>
              </Margin>
            </Col>
          </Row>
          <Row>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="isActive"
                  label={t('page.user.cis.certificationBodies.isActive')}
                  error={errors.isActive}
                >
                  <Checkbox
                    disabled
                    id="isActive"
                    selected={state.isActive}
                    onChange={handleChangeCustom('isActive', 'isActive')}
                  />
                </Field>
              </Margin>
            </Col>
            {state.remoteAssociations?.length > 0 && (
              <Col size={{ xl: 1 / 3 }}>
                <Margin bottom="20">
                  <Field
                    id="remoteAssociations"
                    label={t('page.user.cis.certificationBodies.remoteAssociations')}
                    value={state.remoteAssociations}
                  >
                    <Select
                      id="remoteAssociations"
                      isMulti
                      disabled
                      options={state.remoteAssociations}
                      value={state.remoteAssociations}
                    />
                  </Field>
                </Margin>
              </Col>
            )}
          </Row>

          <Margin bottom={{ xl: 20 }} style={{ padding: '10px', border: '1px solid' }}>
            <Margin bottom="24">
              <Heading size="h5">{t('page.user.cis.organizations.address')}</Heading>
            </Margin>
            <Row>
              <Col size={{ xl: 1 / 3 }}>
                <Margin bottom="20">
                  <Field
                    id="address-line1"
                    label={t('global.addressLineLabel1')}
                    required
                    error={errors.address?.line1}
                  >
                    <input
                      id="address-line1"
                      type="text"
                      placeholder={t('global.addressLinePlaceholder1')}
                      value={state.address?.line1}
                      onChange={handleChangeNestedNative('address', 'line1', 'address.line1')}
                    />
                  </Field>
                </Margin>
              </Col>
              <Col size={{ xl: 1 / 3 }}>
                <Margin bottom="20">
                  <Field
                    id="address-line2"
                    label={t('global.addressLineLabel2')}
                    error={errors.address?.line2}
                  >
                    <input
                      id="address-line2"
                      type="text"
                      placeholder={t('global.addressLinePlaceholder2')}
                      value={state.address?.line2}
                      onChange={handleChangeNestedNative('address', 'line2', 'address.line2')}
                    />
                  </Field>
                </Margin>
              </Col>
              <Col size={{ xl: 1 / 3 }}>
                <Margin bottom="20">
                  <Field
                    id="address-city"
                    label={t('global.cityPlaceholder')}
                    required
                    error={errors.address?.city}
                  >
                    <input
                      id="address-city"
                      type="text"
                      placeholder={t('global.cityPlaceholder')}
                      value={state.address?.city}
                      onChange={handleChangeNestedNative('address', 'city', 'address.city')}
                    />
                  </Field>
                </Margin>
              </Col>
            </Row>
            <Row>
              <Col size={{ xl: 1 / 3 }}>
                <Margin bottom="20">
                  <Field
                    id="address-post-code"
                    label={t('global.postCodeLabel')}
                    required
                    error={errors.address?.postCode}
                  >
                    <input
                      id="address-post-code"
                      type="text"
                      placeholder={t('global.postCodePlaceholder')}
                      value={state.address?.postCode}
                      onChange={handleChangeNestedNative('address', 'postCode', 'address.postCode')}
                    />
                  </Field>
                </Margin>
              </Col>
              <Col size={{ xl: 1 / 3 }}>
                <Margin bottom="20">
                  <Field
                    id="address-country"
                    label={t('page.user.cis.organizations.country')}
                    value={
                      countries.find((option) => option.value === state.address?.country) || ''
                    }
                    clear={() =>
                      handleChangeNestedSelect('address', 'country', 'address.country')(null)
                    }
                    required
                    error={errors.address?.country}
                  >
                    <Select
                      id="address-country"
                      type="text"
                      placeholder={t('page.user.cis.organizations.countryPlaceholder')}
                      value={
                        countries.find((option) => option.value === state.address?.country) || ''
                      }
                      options={countries}
                      onChange={handleChangeNestedSelect('address', 'country', 'address.country')}
                    />
                  </Field>
                </Margin>
              </Col>
              <Col size={{ xl: 1 / 3 }}>
                <Margin bottom="20">
                  <Field
                    id="invoiceAddressSame"
                    label={t('page.user.cis.organizations.invoiceAddressSame')}
                    value={state.invoiceAddressSame}
                    error={errors.invoiceAddressSame}
                  >
                    <Checkbox
                      id="invoiceAddressSame"
                      selected={state.invoiceAddressSame}
                      onChange={handleChangeCustom('invoiceAddressSame', 'invoiceAddressSame')}
                    ></Checkbox>
                  </Field>
                </Margin>
              </Col>
            </Row>
          </Margin>
          {!state.invoiceAddressSame && (
            <Margin bottom={{ xl: 20 }} style={{ padding: '10px', border: '1px solid' }}>
              <Margin bottom="24">
                <Heading size="h5">{t('page.user.cis.organizations.invoiceAddress')}</Heading>
              </Margin>
              <Row>
                <Col size={{ xl: 1 / 3 }}>
                  <Margin bottom="20">
                    <Field
                      id="invoiceAddress-line1"
                      label={t('global.addressLineLabel1')}
                      required
                      error={errors['invoiceAddress.line1']}
                    >
                      <input
                        id="invoiceAddress-line1"
                        type="text"
                        placeholder={t('global.addressLinePlaceholder1')}
                        value={state.invoiceAddress?.line1}
                        onChange={handleChangeNestedNative(
                          'invoiceAddress',
                          'line1',
                          'invoiceAddress.line1',
                        )}
                      />
                    </Field>
                  </Margin>
                </Col>
                <Col size={{ xl: 1 / 3 }}>
                  <Margin bottom="20">
                    <Field
                      id="invoiceAddress-line2"
                      label={t('global.addressLineLabel2')}
                      error={errors['invoiceAddress.line2']}
                    >
                      <input
                        id="invoiceAddress-line2"
                        type="text"
                        placeholder={t('global.addressLinePlaceholder2')}
                        value={state.invoiceAddress?.line2}
                        onChange={handleChangeNestedNative(
                          'invoiceAddress',
                          'line2',
                          'invoiceAddress.line2',
                        )}
                      />
                    </Field>
                  </Margin>
                </Col>
                <Col size={{ xl: 1 / 3 }}>
                  <Margin bottom="20">
                    <Field
                      id="invoiceAddress-city"
                      label={t('global.cityPlaceholder')}
                      required
                      error={errors['invoiceAddress.city']}
                    >
                      <input
                        id="invoiceAddress-city"
                        type="text"
                        placeholder={t('global.cityPlaceholder')}
                        value={state.invoiceAddress?.city}
                        onChange={handleChangeNestedNative(
                          'invoiceAddress',
                          'city',
                          'invoiceAddress.city',
                        )}
                      />
                    </Field>
                  </Margin>
                </Col>
              </Row>
              <Row>
                <Col size={{ xl: 1 / 3 }}>
                  <Margin bottom="20">
                    <Field
                      id="invoiceAddress-post-code"
                      label={t('global.postCodeLabel')}
                      error={errors['invoiceAddress.postCode']}
                    >
                      <input
                        id="invoiceAddress-post-code"
                        type="text"
                        placeholder={t('global.postCodePlaceholder')}
                        value={state.invoiceAddress?.postCode}
                        onChange={handleChangeNestedNative(
                          'invoiceAddress',
                          'postCode',
                          'invoiceAddress.postCode',
                        )}
                      />
                    </Field>
                  </Margin>
                </Col>
                <Col size={{ xl: 1 / 3 }}>
                  <Margin bottom="20">
                    <Field
                      id="invoiceAddress-country"
                      label={t('page.user.cis.organizations.country')}
                      value={
                        countries.find(
                          (option) => option.value === state.invoiceAddress?.country,
                        ) || ''
                      }
                      clear={() =>
                        handleChangeNestedSelect(
                          'invoiceAddress',
                          'country',
                          'invoiceAddress.country',
                        )(null)
                      }
                      required
                      error={errors['invoiceAddress.country']}
                    >
                      <Select
                        id="invoiceAddress-country"
                        type="text"
                        placeholder={t('page.user.cis.organizations.countryPlaceholder')}
                        value={
                          countries.find(
                            (option) => option.value === state.invoiceAddress?.country,
                          ) || ''
                        }
                        options={countries}
                        onChange={handleChangeNestedSelect(
                          'invoiceAddress',
                          'country',
                          'invoiceAddress.country',
                        )}
                      />
                    </Field>
                  </Margin>
                </Col>
              </Row>
            </Margin>
          )}

          <Row>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="cisUsers"
                  label={t('page.user.cis.certificationBodies.user')}
                  error={errors.cisUsers}
                >
                  <DebounceMultiSelect
                    id="cisUsers"
                    placeholder={t('page.user.cis.certificationBodies.userPlaceholder')}
                    value={state.cisUsers}
                    onChange={(options) => {
                      // Preserve the approval flag of already-selected users; a newly picked user
                      // will need approval, so flag it as pending immediately.
                      const annotated = (options || []).map((option) => {
                        const existing = state.cisUsers.find((u) => u.value === option.value);
                        return existing || { ...option, approved: false };
                      });
                      dispatch(updateFieldCreator({ name: 'cisUsers', value: annotated }));
                      if (errors.cisUsers) removeError(clearErrors(errors, 'cisUsers'));
                    }}
                    formatOptionLabel={(option) =>
                      option.approved === false
                        ? `${option.label} ${t('page.user.cis.certificationBodies.pendingApprovalSuffix')}`
                        : option.label
                    }
                    fetchOptions={getUsersOptions}
                  />
                </Field>
              </Margin>
            </Col>
          </Row>

          <Margin bottom="24">
            <Heading size="h5">{t('page.user.cis.certificationBodies.levelDomains')}</Heading>
          </Margin>

          <Margin bottom="20">
            <Field
              id="certificationBodyLevelDomains"
              label={t('page.user.cis.certificationBodies.levelDomains')}
              error={errors.certificationBodyLevelDomains}
            >
              <Select
                id="certificationBodyLevelDomains"
                isMulti
                options={levelDomains}
                value={levelDomains.filter((ld) =>
                  state.certificationBodyLevelDomains.includes(ld.value),
                )}
                onChange={(options) => {
                  const values = options ? options.map((opt) => opt.value) : [];
                  dispatch(
                    updateFieldCreator({
                      name: 'certificationBodyLevelDomains',
                      value: values,
                    }),
                  );
                  if (errors.certificationBodyLevelDomains) {
                    removeError(clearErrors(errors, 'certificationBodyLevelDomains'));
                  }
                }}
                placeholder={t('page.user.cis.certificationBodies.levelDomainsPlaceholder')}
              />
            </Field>
          </Margin>

          <Margin bottom="24">
            <Heading size="h5">{t('page.user.cis.certificationBodies.datesMessage')}</Heading>
          </Margin>

          <Row>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="lcAgreementSigned"
                  label={t('page.user.cis.certificationBodies.lcAgreementSigned')}
                  error={errors.lcAgreementSigned}
                >
                  <Checkbox
                    id="lcAgreementSigned"
                    selected={state.lcAgreementSigned}
                    onChange={handleChangeCustom('lcAgreementSigned', 'lcAgreementSigned')}
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="lcAgreementSignedAt"
                  label={t('page.user.cis.certificationBodies.lcAgreementSignedAt')}
                  error={errors.lcAgreementSignedAt}
                >
                  <Datepicker
                    id="lcAgreementSignedAt"
                    disabled={!state.lcAgreementSigned}
                    selected={state.lcAgreementSignedAt}
                    onChange={handleChangeCustom('lcAgreementSignedAt', 'lcAgreementSignedAt')}
                    dateFormat="yyyy-MM-dd"
                    showYearDropdown
                    showMonthDropdown
                    placeholderText={t('page.user.cis.certificationBodies.datePickerPlaceholder')}
                  />
                </Field>
              </Margin>
            </Col>
          </Row>

          <Row>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="lcValidationSigned"
                  label={t('page.user.cis.certificationBodies.lcValidationSigned')}
                  error={errors.lcValidationSigned}
                >
                  <Checkbox
                    id="lcValidationSigned"
                    selected={state.lcValidationSigned}
                    onChange={handleChangeCustom('lcValidationSigned', 'lcValidationSigned')}
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="lcValidationSignedAt"
                  label={t('page.user.cis.certificationBodies.lcValidationSignedAt')}
                  error={errors.lcValidationSignedAt}
                >
                  <Datepicker
                    id="lcValidationSignedAt"
                    disabled={!state.lcValidationSigned}
                    selected={state.lcValidationSignedAt}
                    onChange={handleChangeCustom('lcValidationSignedAt', 'lcValidationSignedAt')}
                    dateFormat="yyyy-MM-dd"
                    showYearDropdown
                    showMonthDropdown
                    placeholderText={t('page.user.cis.certificationBodies.datePickerPlaceholder')}
                  />
                </Field>
              </Margin>
            </Col>
          </Row>

          <Row>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="deltaAgreementSigned"
                  label={t('page.user.cis.certificationBodies.deltaAgreementSigned')}
                  error={errors.deltaAgreementSigned}
                >
                  <Checkbox
                    id="deltaAgreementSigned"
                    selected={state.deltaAgreementSigned}
                    onChange={handleChangeCustom('deltaAgreementSigned', 'deltaAgreementSigned')}
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="deltaAgreementSignedAt"
                  label={t('page.user.cis.certificationBodies.deltaAgreementSignedAt')}
                  error={errors.deltaAgreementSignedAt}
                >
                  <Datepicker
                    id="deltaAgreementSignedAt"
                    disabled={!state.deltaAgreementSigned}
                    selected={state.deltaAgreementSignedAt}
                    onChange={handleChangeCustom(
                      'deltaAgreementSignedAt',
                      'deltaAgreementSignedAt',
                    )}
                    dateFormat="yyyy-MM-dd"
                    showYearDropdown
                    showMonthDropdown
                    placeholderText={t('page.user.cis.certificationBodies.datePickerPlaceholder')}
                  />
                </Field>
              </Margin>
            </Col>
          </Row>

          <Row>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="iso9001AccreditationSigned"
                  label={t('page.user.cis.certificationBodies.iso9001AccreditationSigned')}
                  error={errors.iso9001AccreditationSigned}
                >
                  <Checkbox
                    id="iso9001AccreditationSigned"
                    selected={state.iso9001AccreditationSigned}
                    onChange={handleChangeCustom(
                      'iso9001AccreditationSigned',
                      'iso9001AccreditationSigned',
                    )}
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="iso9001AccreditationSignedAt"
                  label={t('page.user.cis.certificationBodies.iso9001AccreditationSignedAt')}
                  error={errors.iso9001AccreditationSignedAt}
                >
                  <Datepicker
                    id="iso9001AccreditationSignedAt"
                    disabled={!state.iso9001AccreditationSigned}
                    selected={state.iso9001AccreditationSignedAt}
                    onChange={handleChangeCustom(
                      'iso9001AccreditationSignedAt',
                      'iso9001AccreditationSignedAt',
                    )}
                    dateFormat="yyyy-MM-dd"
                    showYearDropdown
                    showMonthDropdown
                    placeholderText={t('page.user.cis.certificationBodies.datePickerPlaceholder')}
                  />
                </Field>
              </Margin>
            </Col>
          </Row>

          <Row>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="iso17024AccreditationSigned"
                  label={t('page.user.cis.certificationBodies.iso17024AccreditationSigned')}
                  error={errors.iso17024AccreditationSigned}
                >
                  <Checkbox
                    id="iso17024AccreditationSigned"
                    selected={state.iso17024AccreditationSigned}
                    onChange={handleChangeCustom(
                      'iso17024AccreditationSigned',
                      'iso17024AccreditationSigned',
                    )}
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="iso17024AccreditationSignedAt"
                  label={t('page.user.cis.certificationBodies.iso17024AccreditationSignedAt')}
                  error={errors.iso17024AccreditationSignedAt}
                >
                  <Datepicker
                    id="iso17024AccreditationSignedAt"
                    disabled={!state.iso17024AccreditationSigned}
                    selected={state.iso17024AccreditationSignedAt}
                    onChange={handleChangeCustom(
                      'iso17024AccreditationSignedAt',
                      'iso17024AccreditationSignedAt',
                    )}
                    dateFormat="yyyy-MM-dd"
                    showYearDropdown
                    showMonthDropdown
                    placeholderText={t('page.user.cis.certificationBodies.datePickerPlaceholder')}
                  />
                </Field>
              </Margin>
            </Col>
          </Row>

          <Margin bottom="24">
            <Heading size="h5">{t('page.user.cis.certificationBodies.cctMessage')}</Heading>
          </Margin>

          <Row>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="cctAgreementSigned"
                  label={t('page.user.cis.certificationBodies.cctAgreementSigned')}
                  error={errors.cctAgreementSigned}
                >
                  <Checkbox
                    id="cctAgreementSigned"
                    selected={state.cctAgreementSigned}
                    onChange={handleChangeCustom('cctAgreementSigned', 'cctAgreementSigned')}
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="cctAgreementSignedAt"
                  label={t('page.user.cis.certificationBodies.cctAgreementSignedAt')}
                  error={errors.cctAgreementSignedAt}
                >
                  <Datepicker
                    id="cctAgreementSignedAt"
                    disabled={!state.cctAgreementSigned}
                    selected={state.cctAgreementSignedAt}
                    onChange={handleChangeCustom('cctAgreementSignedAt', 'cctAgreementSignedAt')}
                    dateFormat="yyyy-MM-dd"
                    showYearDropdown
                    showMonthDropdown
                    placeholderText={t('page.user.cis.certificationBodies.datePickerPlaceholder')}
                  />
                </Field>
              </Margin>
            </Col>
          </Row>

          <Row>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom={{ xs: 12, md: 0 }}>
                <Button type="button" onClick={() => submit()}>
                  {t('global.saveChangesButton')}
                </Button>
              </Margin>
            </Col>
          </Row>
        </form>
      </Container>
    </PageContent>
  );
};

CertificationBodyDetails.propTypes = {
  certificationBody: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    country: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    }),
    memberAssociation: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    }),
    cbHeadName: PropTypes.string,
    cbHeadEmail: PropTypes.string,
    cbHeadTelephone: PropTypes.string,
    sameAsMaContactData: PropTypes.bool,
    address: PropTypes.number,
    initials: PropTypes.string,
    email: PropTypes.string,
    telephone: PropTypes.string,
    invoiceAddress: PropTypes.number,
    vatId: PropTypes.string,
    website: PropTypes.string,
    isActive: PropTypes.bool,
    lcAgreementSigned: PropTypes.bool,
    lcValidationSigned: PropTypes.bool,
    deltaAgreementSigned: PropTypes.bool,
    iso9001AccreditationSigned: PropTypes.bool,
    iso17024AccreditationSigned: PropTypes.bool,
    ipmaAgreementSigned: PropTypes.bool,
    cctAgreementSigned: PropTypes.bool,
    lcAgreementSignedAt: PropTypes.string,
    lcValidationSignedAt: PropTypes.string,
    deltaAgreementSignedAt: PropTypes.string,
    iso9001AccreditationSignedAt: PropTypes.string,
    iso17024AccreditationSignedAt: PropTypes.string,
    ipmaAgreementSignedAt: PropTypes.string,
    cctAgreementSignedAt: PropTypes.string,
    contactName: PropTypes.string,
    contactEmail: PropTypes.string,
    contactTelephone: PropTypes.string,
    user: PropTypes.shape({
      id: PropTypes.number,
      firstName: PropTypes.string,
      lastName: PropTypes.string,
    }),
  }),
  errors: PropTypes.shape({
    name: PropTypes.string,
    country: PropTypes.string,
    memberAssociation: PropTypes.string,
    cbHeadName: PropTypes.string,
    cbHeadEmail: PropTypes.string,
    cbHeadTelephone: PropTypes.string,
    sameAsMaContactData: PropTypes.string,
    address: PropTypes.string,
    initials: PropTypes.string,
    email: PropTypes.string,
    telephone: PropTypes.string,
    invoiceAddress: PropTypes.string,
    vatId: PropTypes.string,
    website: PropTypes.string,
    isActive: PropTypes.string,
    lcAgreementSigned: PropTypes.string,
    lcValidationSigned: PropTypes.string,
    deltaAgreementSigned: PropTypes.string,
    iso9001AccreditationSigned: PropTypes.string,
    iso17024AccreditationSigned: PropTypes.string,
    ipmaAgreementSigned: PropTypes.string,
    cctAgreementSigned: PropTypes.string,
    lcAgreementSignedAt: PropTypes.string,
    lcValidationSignedAt: PropTypes.string,
    deltaAgreementSignedAt: PropTypes.string,
    iso9001AccreditationSignedAt: PropTypes.string,
    iso17024AccreditationSignedAt: PropTypes.string,
    ipmaAgreementSignedAt: PropTypes.string,
    cctAgreementSignedAt: PropTypes.string,
    contactName: PropTypes.string,
    contactEmail: PropTypes.string,
    contactTelephone: PropTypes.string,
    user: PropTypes.string,
  }),
  updateCertificationBody: PropTypes.func.isRequired,
  removeError: PropTypes.func.isRequired,
  countries: PropTypes.arrayOf(globalPropTypes.ChooseDataPropsTypes).isRequired,
  memberAssociations: PropTypes.arrayOf(globalPropTypes.ChooseDataPropsTypes).isRequired,
  loadCertificationBodiesAttributes: PropTypes.func.isRequired,
  loadCertificationBodyDetails: PropTypes.func.isRequired,
  levelDomains: PropTypes.arrayOf(globalPropTypes.ChooseDataPropsTypes).isRequired,
};

CertificationBodyDetails.defaultProps = {
  certificationBody: {},
  errors: {},
  id: null,
  levelDomains: [],
};

const mapStateToProps = ({ ma, globals, user }) => ({
  certificationBody: ma.cis.certificationBodies.details,
  errors: ma.cis.certificationBodies.editErrors,
  countries: globals.countries,
  memberAssociations: globals.availableMA,
  levelDomains: globals.cis.levelDomains,
  id: user.user.memberAssociation?.certificationBody?.id,
});

const mapDispatchToProps = (dispatch) => ({
  updateCertificationBody: (id, payload, history) =>
    dispatch(updateMaCisCreator(id, 'certificationBodies', payload, history, false)),
  removeError: (payload) => dispatch(setMaCisEditErrorsCreator('certificationBodies', payload)),
  loadCertificationBodiesAttributes: () =>
    dispatch(loadGlobalCisCertificationBodyDetailsAttributesCreator()),
  loadCertificationBodyDetails: (payload, history) =>
    dispatch(loadMaCisDetailsCreator('certificationBodies', payload, history)),
  removeDetails: () => dispatch(removeMaCisDetailsCreator('certificationBodies')),
});

export default connect(mapStateToProps, mapDispatchToProps)(CertificationBodyDetails);
